




























































































































import Vue from "vue";
import moment from "moment";
import { computed, ref } from "@vue/composition-api";
import Modal from "@/components/root/modal/Modal.vue";
import { ModalConfig, UseModal } from "@/components/root/modal";
import { UseEventsCollection } from "@/components/modals/events-modal/use-events";
import { merge_and_fix_cords } from "@/components/modals/events-modal/cords";
import { EventsModalProps } from "@/components/modals/events-modal/model";
import { SharedState } from "@/common/state";
import { AxisType, CoordinateInputTier3, TimeAxisRangeInput } from "@/gql/types";
import { cord3, GqlCache } from "@/gql/lib";
import { GqlCallsKey } from "@/mock/demo/gql.resolver";
import { pxToRem, remToPx } from "@/common/dom.functions";
import { POPULATION_TYPE } from "@/common/models";
import { UsePopup } from "@/components/root/popup/popup.state";
import { uniq } from "@/fp";
import GaIcon from "../card-items/GaIcon.vue";
import { useGenAiPalette } from "../data/design";
import {
  AI_ACTIVITIES,
  AiFileActivityId,
  INFO_CATEGORIES,
  RELATION_CODE,
  activities_for,
  direction_of,
} from "../data/model";
import { get_gen_ai_drill_scope } from "../data/drill-scope";
import { gen_ai_content_items_filter, gen_ai_scope_cord } from "../data/state";
import {
  AI_CONTENT_ITEMS_AVAILABLE,
  get_ai_content_items,
  get_event_by_id,
} from "../data/api";
import { gen_ai_mock_enabled } from "../data/mock-mode";
import { UseCollection } from "@/common/composition/use-collection";
import { gen_ai_table_config as table_config } from "./gen-ai-table.config";
import GaChipFilter from "./events/GaChipFilter.vue";
import GaDateRange from "./events/GaDateRange.vue";
import GaEventsTable from "./events/GaEventsTable.vue";
import GaSelect from "./events/GaSelect.vue";
import GaEventDetailsModal from "./events/GaEventDetailsModal.vue";
import {
  EventMatch,
  EventView,
  LEVEL_OF,
  Level,
  NUM_OF,
  RELATED_EVENT,
  SENSITIVITY_SHORT,
  SEVERITY_LABEL,
  content_item_to_event_view,
  to_event_view,
} from "./events/event-view";
import { RowAction, run_row_action } from "./events/row-actions";

/** the calls the events list is cached under, cleared before a refetch */
const EVENT_CACHE_TAGS: GqlCallsKey[] = ["exposure-events", "tier3-events"];

/** a file's columns in the CSV report, read off the `aiContentItems` rows */
const csv_column = (name: string, key: string, path: string) => ({
  name,
  key,
  spanMin: "12rem",
  spanMax: "1fr",
  sortable: false,
  filterable: false,
  jsPath: "data.aiContentItems[]." + path,
});
const CONTENT_ITEM_CSV_COLUMNS = [
  csv_column("User", "exposedTo", "exposedTo[*].name"),
  csv_column("Content", "name", "name"),
  csv_column("AI Agent", "exposedBy", "exposedBy.name"),
  csv_column("Risk", "severity", "severity"),
  csv_column("Information Type", "infoLabel", "labels.infoLabel.name"),
  csv_column("Path", "path", "path"),
  csv_column("Sensitivity", "sensitivityLevel", "sensitivityLevel"),
  csv_column("Date", "createdAt", "createdAt"),
  csv_column("Application", "sourceApplication", "sourceApplication"),
];

const ALL_ACTIVITIES = "All Activities";
const ALL_RISK_TYPES = "All Risk Types";
const RISK_KEYS: Level[] = ["low", "medium", "high"];
const SENSITIVITY_KEYS: Level[] = ["low", "medium", "high", "none"];

export default Vue.extend({
  components: {
    Modal,
    GaIcon,
    GaChipFilter,
    GaDateRange,
    GaEventsTable,
    GaSelect,
  },
  props: ["modalData"],
  setup(props, { emit }) {
    const T = useGenAiPalette();
    const modal_data = computed(() => props.modalData as EventsModalProps);
    const scope = get_gen_ai_drill_scope();

    const time_frame = computed(() => {
      const { from, type } = SharedState.selectedTimeFrameOption.value;
      return { type, values: [from] } as TimeAxisRangeInput;
    });

    // ── Risk severity: the query's own filter ──────────────────────────
    /**
     * Seeded from the drill-down: the risk cards pass the levels their number
     * counted, as a Severity coordinate. The Visibility cards pass none, which
     * means every event including the ones with no risk — the prototype's
     * "high, medium, low, none". Picking chips then filters to exactly those.
     */
    const passed_severities = (modal_data.value.coordinates || [])
      .flatMap((c) => [c.x, c.y])
      .filter((a) => a && a.type === AxisType.Severity)
      .flatMap((a) => a.values || [])
      .map((v) => LEVEL_OF[+v])
      .filter(Boolean) as Level[];
    const severity_set = ref<Level[]>(
      passed_severities.length ? uniq(passed_severities) : [...RISK_KEYS, "none"]
    );
    const severity_keys = computed(() =>
      RISK_KEYS.filter((k) => severity_set.value.includes(k))
    );

    // ── Risk type: the app's share types, as its own filter offers them ─
    const share_types: string[] = uniq(
      SharedState.compliance_insights.value.map((s: any) => s.shareType)
    ).filter(Boolean) as string[];
    const risk_type_options = [
      { value: ALL_RISK_TYPES, label: ALL_RISK_TYPES },
      ...share_types.map((t) => ({
        value: t,
        label: t === "anonymous" ? "Shared via anonymous link" : t,
      })),
    ];
    const risk_type = ref(ALL_RISK_TYPES);

    // ── Activity: live, the backend filters on it (the relation's codes) ─
    const locked_activity = scope && scope.relation
      ? AI_ACTIVITIES.find((a) => a.id === scope.relation) || null
      : null;
    // A card that counted one direction opens a list of that direction, and
    // the filter narrows within it. Offering the other direction's activities
    // would AND two relation sets that never meet, and empty the list.
    const offered_activities =
      scope && scope.direction && !scope.relation
        ? activities_for(scope.direction)
        : AI_ACTIVITIES;
    const activity_options = [
      { value: ALL_ACTIVITIES, label: ALL_ACTIVITIES },
      ...offered_activities.map((a) => ({ value: a.id, label: a.name })),
    ];
    const activity = ref<string>(locked_activity ? locked_activity.id : ALL_ACTIVITIES);

    /** what the list asks the backend for, over the drill-down's own coordinates */
    const filter_cords = computed<CoordinateInputTier3[]>(() => {
      const z = time_frame.value;
      const cords: CoordinateInputTier3[] = [];
      const all_levels = SENSITIVITY_KEYS.every((k) => severity_set.value.includes(k));
      if (!all_levels) {
        cords.push({
          x: cord3(AxisType.None, []),
          y: {
            type: AxisType.Severity,
            values: severity_set.value.map((k) => String(NUM_OF[k])),
          },
          z,
        } as CoordinateInputTier3);
      }
      if (activity.value !== ALL_ACTIVITIES) {
        // overrides the drill-down's relation codes, keeps its category
        const relation = gen_ai_scope_cord({ relation: activity.value as AiFileActivityId }, z);
        if (relation) cords.push({ ...relation, y: cord3(AxisType.None, []) } as CoordinateInputTier3);
      }
      if (risk_type.value !== ALL_RISK_TYPES) {
        const levels = severity_set.value.map((k) => NUM_OF[k]);
        const l0s = SharedState.l0InfoTypes.value.map((d: any) => d.id);
        const ids = SharedState.compliance_insights.value
          .filter(
            (f: any) =>
              levels.includes(f.severityLevel) &&
              f.shareType === risk_type.value &&
              l0s.includes(f.infoTypeId)
          )
          .map((d: any) => d.id);
        if (ids.length) {
          cords.push({
            x: cord3(AxisType.None, []),
            y: { type: AxisType.Insight, values: ids },
            z,
          } as CoordinateInputTier3);
        }
      }
      return cords;
    });

    // ── Date range: sent to the backend and applied to loaded rows ─────
    const applied_from = ref<number | null>(null);
    const applied_to = ref<number | null>(null);
    const scope_start = computed(() => {
      const option = SharedState.selectedTimeFrameOption.value;
      return option.type === "Week"
        ? moment(option.from).isoWeekday(1).startOf("day").valueOf()
        : moment(option.from).startOf("day").valueOf();
    });
    const scope_end = computed(() => {
      const option: any = SharedState.selectedTimeFrameOption.value;
      const now = Date.now();
      if (option.to) return Math.min(moment(option.to).endOf("day").valueOf(), now);
      if (option.type === "Week") {
        return Math.min(
          moment(option.from).isoWeekday(1).add(6, "days").endOf("day").valueOf(),
          now
        );
      }
      return Math.min(moment(option.from).endOf("month").valueOf(), now);
    });
    const date_filter = () => ({
      fromDate: applied_from.value !== null ? moment(applied_from.value).format("YYYY-MM-DD") : null,
      toDate: applied_to.value !== null ? moment(applied_to.value).format("YYYY-MM-DD") : null,
    });

    const {
      items: events,
      is_loading: events_loading,
      has_more: events_has_more,
      clear: clear_events,
      load_items: load_events,
      load_more_items: load_more,
    } = UseEventsCollection(
      // the drill-downs leave the month empty, and the list returns nothing
      // for an empty month: every coordinate takes the dashboard's frame
      () =>
        merge_and_fix_cords(
          (modal_data.value.coordinates || []).map((c) => ({ ...c, z: time_frame.value })),
          [...filter_cords.value]
        ),
      {
        clusterUID: modal_data.value.clusterUID,
        statusFilters: () => ({
          mark: null,
          justification: null,
          externalEventTypes: null,
          ...date_filter(),
        }),
      }
    );

    // ── The content drill-downs list files, not events ─────────────────
    /**
     * Every card that counts content lists the files of the matching AI
     * events (`aiContentItems`, see api.ts §5). An employee's own interactions (Widget
     * 5) are not a content drill-down and stay on the events list. The demo
     * rows are events, so mock mode keeps the events list too.
     *
     * There is no fallback to events: a failed request shows as a failure,
     * never as a list of parent events standing in for the files.
     */
    const CONTENT_WIDGETS = ["severity", "risk-category", "accessed", "matrix"];
    const content_mode = ref(
      AI_CONTENT_ITEMS_AVAILABLE &&
        !!scope &&
        CONTENT_WIDGETS.includes(scope.widget || "") &&
        !scope.actor &&
        !gen_ai_mock_enabled()
    );

    /** what the list is asked for, from the card's scope and the filters now set */
    const items_filter = () =>
      gen_ai_content_items_filter(
        {
          relation:
            activity.value !== ALL_ACTIVITIES
              ? (activity.value as AiFileActivityId)
              : (scope && scope.relation) || undefined,
          direction: scope ? scope.direction : undefined,
          category: scope ? scope.category : undefined,
          // the risk levels the list shows, in the relation's scope
          severities: severity_set.value.map((k) => NUM_OF[k]),
          // Sensitivity is asked for with the virtual labels, as the card
          // counted it: the chips (seeded from the card's slider) pick the
          // stop — all four is the categories, else the widest graded level.
          sensitivities: [...sensitivity_set.value],
        },
        time_frame.value
      );

    /** set when an aiContentItems request fails; the list says so */
    const content_error = ref("");
    const content_items = UseCollection<any, { cursor: string }>(async ({ cursor }) => {
      const filter = items_filter();
      if (
        !filter ||
        !filter.labels.length ||
        !sensitivity_set.value.length ||
        !filter.severities.length
      ) {
        return { success: true, value: { cursor: "", objects: [] } };
      }
      const res = await get_ai_content_items({ ...filter, cursor });
      if (!res.success) {
        content_error.value = cursor
          ? "Couldn't load more files. The request failed."
          : "Couldn't load the files. The request failed.";
        console.error("[gen-ai] aiContentItems failed", {
          code: (res as any).code,
          body: (res as any).body,
          filter: { ...filter, cursor },
        });
      }
      return res;
    });

    /**
     * Rows the list filters out itself (risk, sensitivity) can leave a page
     * nearly empty, so pages keep coming until there is a screenful to show
     * or nothing left — within a few pages, so a narrow filter cannot read the
     * whole month in one go.
     */
    const FILL_ROWS = 20;
    const FILL_PAGES = 10;
    const fill = async (target: number) => {
      for (let page = 0; page < FILL_PAGES; page++) {
        if (rows.value.length >= target || !content_items.has_more.value || content_error.value) {
          return;
        }
        await content_items.load_more_items();
      }
    };

    /** the first page of content items, then enough to fill the list */
    const load_content_items = async () => {
      content_error.value = "";
      // a failed reload must not leave the previous filter's rows on screen
      content_items.clear();
      await content_items.load_items({ cursor: "" });
      await fill(FILL_ROWS);
    };


    const is_loading = computed(() =>
      content_mode.value ? content_items.is_loading.value : events_loading.value
    );
    const has_more = computed(() =>
      content_mode.value ? content_items.has_more.value : events_has_more.value
    );

    /**
     * An employee's interactions list (task 25091) is the regular list without
     * its Activity Type column; the Activity Type filter stays.
     */
    const show_activity_column = !(scope && scope.actor);

    const reload = async () => {
      if (content_mode.value) {
        GqlCache.removeByTag("ms-365-gen-ai.ai-content-items");
        await load_content_items();
        return;
      }
      EVENT_CACHE_TAGS.forEach((tag) => GqlCache.removeByTag(tag));
      clear_events();
      await load_events({ cursor: "", externalEventTypes: null, ...date_filter() });
    };

    // ── Sensitivity and activity: the backend cannot filter on either for
    //    events yet, so they narrow the loaded rows ────────────────────
    const sensitivity_set = ref<Level[]>(
      scope && scope.sensitivities && scope.sensitivities.length
        ? uniq(scope.sensitivities.map((n) => LEVEL_OF[n]).filter(Boolean) as Level[])
        : [...SENSITIVITY_KEYS]
    );
    const sensitivity_keys = computed(() => [...sensitivity_set.value]);


    const scope_category = INFO_CATEGORIES.find((c) => scope && c.id === scope.category) || null;
    const info_type = scope_category ? scope_category.name : "All Information Types";

    /** the one activity the list was asked for, if it was asked for one */
    const pinned_activity = computed(() =>
      AI_ACTIVITIES.find((a) => a.id === activity.value) || null
    );

    /**
     * The relation(s) the list is asking the backend for right now, as the UI
     * names them: the picked activity, else the card's direction. Empty when
     * the list asks for no relation in particular.
     */
    const relation_scope = computed(() => {
      if (pinned_activity.value) return pinned_activity.value.name;
      if (scope && scope.direction) {
        return activities_for(scope.direction)
          .filter((a) => RELATION_CODE[a.id])
          .map((a) => a.name)
          .join(", ");
      }
      return "";
    });
    /**
     * What a file row reads as: a file names no relation, but a list asked for
     * one relation is that relation's; a list of several names them all.
     */
    const content_relation = computed(() => {
      if (pinned_activity.value) {
        return { id: pinned_activity.value.id, name: pinned_activity.value.name };
      }
      const asked =
        scope && scope.direction
          ? activities_for(scope.direction).filter((a) => RELATION_CODE[a.id])
          : [];
      if (asked.length === 1) return { id: asked[0].id, name: asked[0].name };
      return relation_scope.value ? { id: null, name: relation_scope.value } : null;
    });

    const match_for = (level: EventMatch["level"], relation: string): EventMatch => ({
      level,
      relation,
      category: scope_category ? scope_category.name : "",
      period: SharedState.selectedTimeFrameDisplayName.value || "",
    });

    const rows = computed<EventView[]>(() =>
      (content_mode.value
        ? content_items.items.value.map((item) =>
            content_item_to_event_view(item, content_relation.value)
          )
        : events.value.map(to_event_view))
        // A live event does not say which relation it was (its subtype is
        // "NonHumanInteraction"), and it can group several files of which
        // only some matched. The list's filter is why it is here, not what
        // it is: it reads as a related event, with the filter beside it,
        // rather than being relabelled as that activity (see EventMatch).
        .map((e) => {
          // a content item is the match itself, whatever its relation reads as
          if (e.activity_id || content_mode.value) {
            return relation_scope.value ? { ...e, match: match_for("item", e.activity) } : e;
          }
          if (!relation_scope.value) return e;
          return {
            ...e,
            activity: RELATED_EVENT,
            match: match_for("event", relation_scope.value),
          };
        })
        // An events list is not asked for by sensitivity, so its rows are
        // narrowed on their own (an event's most sensitive file). A content
        // list was asked for it with the virtual labels, and is not filtered
        // again on the file's own sensitivity field: the two can disagree, and
        // then the list hid the very files the card counted.
        .filter((e) => content_mode.value || sensitivity_set.value.includes(e.sensitivity))
        .filter((e) => {
          // The backend already narrowed live rows to the activity, and they
          // name none of their own, so they stay. The demo rows carry theirs.
          if (activity.value !== ALL_ACTIVITIES) {
            return !e.activity_id || e.activity_id === activity.value;
          }
          // a card that counted one direction lists that direction; a row whose
          // activity could not be read is kept rather than silently dropped
          if (scope && scope.direction && e.activity_id) {
            return direction_of(e.activity_id) === scope.direction;
          }
          return true;
        })
        .filter((e) => applied_from.value === null || e.ts >= (applied_from.value as number))
        .filter((e) => applied_to.value === null || e.ts <= (applied_to.value as number))
    );

    const on_severity = async (keys: string[]) => {
      severity_set.value = keys as Level[];
      await reload();
    };
    const on_sensitivity = async (keys: string[]) => {
      sensitivity_set.value = keys as Level[];
      // a content list asks again with the new stop's labels
      if (content_mode.value) await reload();
    };
    const on_risk_type = async (value: string) => {
      risk_type.value = value;
      await reload();
    };
    const on_activity = async (value: string) => {
      activity.value = value;
      await reload();
    };
    const on_date = async (range: { from_ts: number; to_ts: number } | null) => {
      applied_from.value = range ? range.from_ts : null;
      applied_to.value = range ? range.to_ts : null;
      await reload();
    };

    const load_more_events = async () => {
      if (!has_more.value) return;
      if (content_mode.value) {
        const target = rows.value.length + FILL_ROWS;
        await content_items.load_more_items();
        await fill(target);
      } else await load_more();
    };

    // ── Row menu and details ───────────────────────────────────────────
    /**
     * A content item names its event (`eventRefId`); the details and the row
     * actions work on the event, read with the singular `event(id:)`. If it
     * cannot be read, the item's own view stands in.
     */
    const as_event = async (e: EventView): Promise<EventView> => {
      if (!content_mode.value || !e.raw || !e.raw.eventRefId) return e;
      const res = await get_event_by_id({ id: e.raw.eventRefId });
      if (!res.success || !res.value) return e;
      // the event is the item's parent: it keeps the item's relation, and
      // names the file that matched among the ones it groups
      return {
        ...to_event_view(res.value),
        activity_id: e.activity_id,
        activity: e.activity,
        match: { ...match_for("item", e.activity), file_id: e.id, file_name: e.content.name },
      };
    };

    const on_row_action = async (e: EventView, action: RowAction) => {
      if ((await run_row_action(await as_event(e), action)) === "close") emit("cancel");
    };

    let details_open = false;
    const open_details = async (e: EventView) => {
      if (details_open) return;
      details_open = true;
      try {
        const res: any = await UseModal().show(GaEventDetailsModal, { event: await as_event(e) });
        if (res && res.type === "success" && res.msg && res.msg.action) {
          await on_row_action(e, res.msg.action);
        }
      } finally {
        details_open = false;
      }
    };

    // ── CSV: the app's own report, over every row the query matches ────
    const table_config_for_csv = computed(() => ({
      ...table_config,
      columns: content_mode.value
        ? CONTENT_ITEM_CSV_COLUMNS
        : table_config.columns.filter(
            (c: any) => c.key !== "justification" && Boolean(c.jsPath)
          ),
    }));
    const export_table_as_csv = async (event: MouseEvent) => {
      if (SharedState.is_biz_user.value && SharedState.restrictions.is_restricted_app("reports")) {
        SharedState.restrictions.show_restrictions_modal("app", "Reports");
        return;
      }
      const { state: csv_state } = await import("@/views/reports/data/csv/state.csv");
      const payload = {
        possible_call_tags: content_mode.value
          ? ["ms-365-gen-ai.ai-content-items"]
          : ["exposure-events", "tier3-events"],
        table_config: table_config_for_csv.value,
        title: modal_data.value.title,
        tags: {
          label: info_type,
          severity: severity_set.value.map((k) => NUM_OF[k]).join(","),
          application: "",
          entity_type: POPULATION_TYPE.EVENT,
        },
      };
      if (SharedState.FeatureHelper.is_enabled("csv_report_scheduler")) {
        const res = await UsePopup.showActionPopup({
          type: "TextAndIconDisplayPopup",
          items: [
            { icon: "CSV", text: "Generate csv report" },
            { icon: "Calendar", text: "Schedule csv report" },
          ],
          width: remToPx(15),
          event,
          position: "bottom-left",
        });
        if (res.type == "canceled") return;
        // @ts-ignore
        if (res.action == "Schedule csv report") csv_state.schedule_gql_csv_report(payload);
        // @ts-ignore
        else csv_state.create_gql_report(payload);
      } else {
        // @ts-ignore
        csv_state.create_gql_report(payload);
      }
    };

    const modal_config: ModalConfig = {
      title: "",
      desc: "",
      blank: true,
      transparentBg: true,
      backdropDismiss: true,
      width: pxToRem(Math.min(1240, innerWidth * 0.92)) + "rem",
      height: pxToRem(Math.min(920, innerHeight * 0.92)) + "rem",
    };

    const severity_levels = computed(() =>
      RISK_KEYS.map((k) => ({
        key: k,
        label: SEVERITY_LABEL[k],
        mark: "⇄",
        trigger: { bg: T.value.severity[k].chipBg, text: T.value.severity[k].chipText },
        menu: { bg: T.value.severity[k].dot, text: "#FFFFFF" },
      }))
    );
    const sensitivity_levels = computed(() =>
      SENSITIVITY_KEYS.map((k) => ({
        key: k,
        label: SENSITIVITY_SHORT[k],
        mark: k === "none" ? "—" : SENSITIVITY_SHORT[k].charAt(0),
        trigger: { bg: T.value.sensitivity[k].dot, text: "#FFFFFF" },
        menu: { bg: T.value.sensitivity[k].dot, text: "#FFFFFF" },
      }))
    );

    if (content_mode.value) load_content_items();
    else load_events({ cursor: "" });

    return {
      content_items_mode: content_mode,
      content_error,
      show_activity_column,
      T,
      modal_data,
      modal_config,
      has_reports_enabled: SharedState.FeatureHelper.is_enabled("reports"),
      rows,
      is_loading,
      has_more,
      load_more_events,
      severity_levels,
      severity_keys,
      on_severity,
      sensitivity_levels,
      sensitivity_keys,
      on_sensitivity,
      risk_type,
      risk_type_options,
      on_risk_type,
      info_type,
      activity,
      activity_options,
      // a string, never null: the composition-api plugin cannot take a null
      // binding and drops every one after it
      locked_activity_name: locked_activity ? locked_activity.name : "",
      on_activity,
      scope_start,
      scope_end,
      applied_from,
      applied_to,
      on_date,
      open_details,
      on_row_action,
      export_table_as_csv,
    };
  },
});
